import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { Configure, Index, InstantSearch, connectStateResults } from 'react-instantsearch-dom';

import searchClient from '../../algoliaConfig';
import CustomSearchBox from '../CustomSearchBox/CustomSearchBox';
import { updateGetToSectionAlgolia } from '../../redux/slices/KnowDivInScreen';

// BUSCADOR DE LA DOCUMENTACIÓN (cabecera)
// Antes vivía dentro de ApiSideBar ("Find anything"). Estilos en assets/scss/pages/_docsCursor.scss

// Por debajo de este ancho la cabecera pasa a modo compacto (ver _docsCursor.scss)
const DESKTOP_MIN_WIDTH = 1200;

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform);

const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className} aria-hidden="true" width="14" height="14" viewBox="0 0 512 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
  </svg>
);

const ProductHit = ({ hit, isSelected, onClick, onMouseEnter }) => (
  <div
    className={`docs-search-item ${isSelected ? 'is-selected' : ''}`}
    onClick={() => onClick(hit)}
    onMouseEnter={onMouseEnter}
  >
    <svg className="docs-search-item__icon" aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M0 2.75C0 1.784.784 1 1.75 1H5.5c1.005 0 1.904.456 2.5 1.173A3.243 3.243 0 0 1 10.5 1h3.75c.966 0 1.75.784 1.75 1.75v10.5A1.75 1.75 0 0 1 14.25 15H1.75A1.75 1.75 0 0 1 0 13.25V2.75ZM7.25 13.5V4.25A1.75 1.75 0 0 0 5.5 2.5H1.75a.25.25 0 0 0-.25.25v10.5c0 .138.112.25.25.25h5.5Zm1.5 0h5.5a.25.25 0 0 0 .25-.25V2.75a.25.25 0 0 0-.25-.25h-1.754v3.75a.75.75 0 0 1-1.5 0V2.5H10.5a1.75 1.75 0 0 0-1.75 1.75v9.25Z"></path></svg>
    <span className="docs-search-item__title">{hit.title}</span>
    <span className="docs-search-item__category">{hit.category}</span>
  </div>
);

// Mensaje cuando no hay resultados
const NoResults = connectStateResults(({ searchResults }) => {
  if (searchResults && searchResults.nbHits === 0) {
    return <div className="docs-search-empty">No results found.</div>;
  }
  return null;
});

// Lista de resultados con navegación por teclado (↑ ↓ Enter)
const CustomStateResults = connectStateResults(({ searchState, searchResults, handleClick }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Al cambiar la búsqueda, se vuelve a seleccionar el primer resultado
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchState && searchState.query]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!searchResults || searchResults.nbHits === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, searchResults.hits.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      } else if (event.key === 'Enter' && selectedIndex >= 0 && searchResults.hits[selectedIndex]) {
        handleClick(searchResults.hits[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedIndex, searchResults, handleClick]);

  if (!searchResults || searchResults.nbHits === 0) {
    return <NoResults />;
  }

  return (
    <div className="docs-search-list">
      {searchResults.hits.map((hit, index) => (
        <ProductHit
          key={hit.objectID}
          hit={hit}
          isSelected={index === selectedIndex}
          onClick={handleClick}
          onMouseEnter={() => setSelectedIndex(index)}
        />
      ))}
    </div>
  );
});

const DocsSearch: FunctionComponent = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // El buscador se centra sobre la columna de texto,
  // no sobre la ventana. La posición de la columna depende del ancho de la
  // ventana, de la columna derecha y de la barra de scroll, así que se mide.
  useEffect(() => {
    const btn = triggerRef.current;
    if (!btn) return;

    const align = () => {
      const nav = btn.closest('#devNav') as HTMLElement | null;
      const column = document.querySelector('.docs-page__main .contentMarginX') || document.querySelector('.docs-page__main');
      if (!nav || !column || !window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches) {
        btn.classList.remove('is-aligned');
        return;
      }
      const navRect = nav.getBoundingClientRect();
      const colRect = column.getBoundingClientRect();
      const half = btn.offsetWidth / 2;

      // Sin pisar los enlaces de la izquierda ni los botones de la derecha
      const start = nav.querySelector('.dp-navbar-start');
      const end = nav.querySelector('.dp-navbar-end');
      const min = (start ? start.getBoundingClientRect().right - navRect.left : 0) + 16 + half;
      const max = (end ? end.getBoundingClientRect().left - navRect.left : navRect.width) - 16 - half;

      const center = Math.min(Math.max(colRect.left + colRect.width / 2 - navRect.left, min), max);
      nav.style.setProperty('--docs-search-center', `${Math.round(center)}px`);
      btn.classList.add('is-aligned');
    };

    // Este efecto se ejecuta antes que el de BaseLayout, que es el que añade la
    // clase body.devportal-dashboard de la que depende la maquetación de docs:
    // se mide también tras el siguiente pintado y cuando BaseLayout avisa
    // con el evento 'themeChanged' (lo lanza justo después de añadir la clase)
    align();
    const frame = requestAnimationFrame(() => requestAnimationFrame(align));
    window.addEventListener('resize', align);
    window.addEventListener('themeChanged', align);
    // La columna cambia de ancho sin que cambie la ventana (al aparecer la
    // barra de scroll cuando carga el contenido): se vigila la propia columna
    const RO = (window as any).ResizeObserver;
    const observer = RO ? new RO(align) : null;
    const main = document.querySelector('.docs-page__main');
    if (observer && main) observer.observe(main);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', align);
      window.removeEventListener('themeChanged', align);
      if (observer) observer.disconnect();
    };
  }, []);

  // Con el buscador abierto la página de detrás no hace scroll. Bootstrap solo
  // bloquea <body>, pero en el portal el que hace scroll es <html>. Se guarda el
  // valor anterior por si el menú o el panel lateral ya lo habían bloqueado
  useEffect(() => {
    if (!show) return;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { html.style.overflow = prevOverflow; };
  }, [show]);

  // Atajo Ctrl+K / ⌘K para abrir el buscador
  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setShow(true);
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  const handleClick = (hit) => {
    const url = `/${hit.basepath}/#${hit.path}`;
    const before = window.location.pathname + window.location.hash;
    history.push(url);

    // Mismo resultado que la última vez (la URL no cambia): no se dispara ningún
    // efecto de scroll, así que se busca la sección y se hace aquí
    if (window.location.pathname + window.location.hash === before) {
      const target = document.getElementById(hit.path)
        || Array.from(document.querySelectorAll(".contentMarginX > [class*='col-'] > [id]")).find((el) => el.id.includes(hit.path));
      if (target) target.scrollIntoView();
    }

    // Sección a la que debe hacer scroll ContentDoc
    const sectionAlgolia = hit.category.toLowerCase() + '-' + hit.path;
    dispatch(updateGetToSectionAlgolia(sectionAlgolia));

    handleClose();
  };

  return (
    <>
      <button ref={triggerRef} type="button" className="docs-search-trigger" onClick={handleShow} aria-label="Search docs">
        <SearchIcon className="docs-search-trigger__icon" />
        <span className="docs-search-trigger__text">Search docs...</span>
        <kbd className="docs-search-kbd docs-search-trigger__kbd">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        id="docs-search-modal"
        dialogClassName="docs-search-dialog"
        backdropClassName="docs-search-backdrop"
        // Al cerrar, Bootstrap devuelve el foco al botón y, si se cerró con
        // teclado (Esc), se queda marcado con el anillo de :focus-visible
        restoreFocus={false}
        // El modal de Bootstrap se enfoca a sí mismo al abrirse y le quita el foco
        // al autoFocus del input: sin esto, Ctrl+K abría pero no se podía escribir
        onEntered={() => {
          const input = document.querySelector('.docs-search-dialog .search-input') as HTMLInputElement | null;
          if (input) input.focus();
        }}
      >
        <InstantSearch indexName="Wiki" searchClient={searchClient} insights={true}>
          <CustomSearchBox />
          <div className="docs-search-results">
            <Index indexName="Wiki">
              <Configure hitsPerPage={6} />
              <CustomStateResults handleClick={handleClick} />
            </Index>
          </div>
        </InstantSearch>

        <div className="docs-search-footer">
          <span><kbd className="docs-search-kbd">↑</kbd><kbd className="docs-search-kbd">↓</kbd> Navigate</span>
          <span><kbd className="docs-search-kbd">↵</kbd> Open</span>
          <span><kbd className="docs-search-kbd">Esc</kbd> Close</span>
        </div>
      </Modal>
    </>
  );
};

export default DocsSearch;
